import mermaid from 'mermaid';
import { useEffect, useRef, useState } from 'react';

interface MermaidDiagramProps {
  chart: string;
  id?: string;
}

mermaid.initialize({
  startOnLoad: true,
  theme: 'default',
  securityLevel: 'loose',
  themeVariables: {
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  },
  flowchart: {
    htmlLabels: true,
    curve: 'basis',
  },
});

// eslint-disable-next-line react/prop-types
export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart, id }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current || !chart) return;

      const diagramId = id ?? `mermaid-${Math.random().toString(36).substr(2, 9)}`;
      
      try {
        // Clear any previous errors
        setError('');
        
        // Check if dark mode is enabled
        const isDark = document.documentElement.classList.contains('dark');
        
        // Update mermaid theme based on dark mode
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? 'dark' : 'default',
          securityLevel: 'loose',
          themeVariables: {
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
            darkMode: isDark,
            background: isDark ? '#1f2937' : '#ffffff',
            primaryTextColor: isDark ? '#e5e7eb' : '#111827',
            primaryColor: isDark ? '#1e40af' : '#3b82f6',
            primaryBorderColor: isDark ? '#1e3a8a' : '#2563eb',
          },
        });

        const { svg: renderedSvg } = await mermaid.render(diagramId, chart);
        setSvg(renderedSvg);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError('Failed to render diagram. Please check the syntax.');
      }
    };

    void renderDiagram();
  }, [chart, id]);

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
          <code>{chart}</code>
        </pre>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="mermaid-container flex justify-center items-center p-4 bg-white dark:bg-gray-900 rounded-lg overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};